import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, fmtDate } from '../../components/ui';
import { Panel } from '../../components/charts';
import { fmtInt } from './Week';

/* ============================================================
   Orang, dan pemetaannya ke resource programme.

   Ini satu-satunya halaman yang bisa mengubah cakupan PIC dari nol.
   Programme menyebut kru ("BMS Cabling Crew"); di sinilah kru itu
   diberi nama orang, dan sejak itu papan mingguan punya sesuatu
   untuk ditampilkan.

   Penugasan orang berubah harian dan programme diperbarui bulanan,
   jadi peta ini sengaja hidup di sini dan bukan di dalam berkas
   MS Project — yang lebih sering berubah tidak boleh dikunci pada
   yang lebih jarang.
   ============================================================ */

type Assignee = {
  id: string;
  isPrimary: boolean;
  fromDate: string | null;
  toDate: string | null;
  resource: { id: string; name: string; projectId: string };
  location: { id: string; name: string } | null;
};

type Person = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  org: string | null;
  role: string | null;
  active: boolean;
  assignees: Assignee[];
};

const BLANK = { name: '', org: '', role: '', email: '', phone: '' };

export function People({ projectId }: { projectId: string }) {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [map, setMap] = useState({ personId: '', resourceId: '', isPrimary: true });

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ people: Person[] }>('/team/people');
      setPeople(r.people);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load people.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function addPerson(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      const created = await api.post<{ id: string; name: string }>('/team/people', {
        name: form.name,
        org: form.org || null,
        role: form.role || null,
        email: form.email || null,
        phone: form.phone || null,
      });
      setForm(BLANK);
      setMap({ ...map, personId: created.id });
      setNotice(`${created.name} terdaftar. Petakan ke resource supaya namanya muncul di papan mingguan.`);
      await load();
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Gagal menambah orang.');
    } finally {
      setBusy(false);
    }
  }

  async function addMapping(e: React.FormEvent) {
    e.preventDefault();
    if (!map.personId || !map.resourceId.trim()) return;
    setBusy(true);
    try {
      await api.post('/team/assign', {
        personId: map.personId,
        resourceId: map.resourceId.trim(),
        isPrimary: map.isPrimary,
      });
      setMap({ ...map, resourceId: '' });
      setNotice('Pemetaan tersimpan.');
      await load();
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Gagal memetakan.');
    } finally {
      setBusy(false);
    }
  }

  async function unmap(assigneeId: string) {
    setBusy(true);
    try {
      await api.del(`/team/assign/${assigneeId}`);
      setNotice('Pemetaan dilepas.');
      await load();
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Gagal melepas pemetaan.');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!people) return <Loading rows={4} />;

  /* Belum ada endpoint daftar resource, jadi satu-satunya nama
     resource yang diketahui halaman ini adalah yang sudah pernah
     dipetakan seseorang. Saran di bawah karena itu tidak pernah
     lengkap, dan kolomnya tetap menerima ID mentah. */
  const known = new Map<string, string>();
  for (const p of people) {
    for (const a of p.assignees) {
      if (a.resource.projectId === projectId) known.set(a.resource.id, a.resource.name);
    }
  }

  const mapped = [...new Set(people.flatMap((p) => p.assignees.filter((a) => a.resource.projectId === projectId).map((a) => a.resource.id)))];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Orang</h1>
          <p>
            Programme menyebut kru. Papan mingguan menyebut orang. Peta di halaman ini yang
            menghubungkan keduanya.
          </p>
        </div>
      </div>

      {notice && <div className="banner">{notice}</div>}

      {people.length === 0 && (
        <div className="banner">
          Belum ada satu pun orang terdaftar, jadi cakupan PIC proyek ini nol dan setiap aktivitas
          minggu ini tidak punya nama. Dua langkah: tambah orang di bawah, lalu petakan ke resource
          yang dipegangnya.
        </div>
      )}

      <div className="grid-2">
        <Panel
          title="Tambah orang"
          note="Nama wajib. Sisanya boleh menyusul — yang kosong tetap kosong, tidak diisi tebakan."
        >
          <form onSubmit={addPerson}>
            <div className="filters">
              <input
                className="input"
                placeholder="Nama"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
              <input
                className="input"
                placeholder="Organisasi"
                value={form.org}
                onChange={(e) => setForm({ ...form, org: e.target.value })}
              />
              <input
                className="input"
                placeholder="Peran — misalnya Supervisor ME"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              />
              <input
                className="input"
                type="email"
                placeholder="Surel"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <input
                className="input"
                placeholder="Telepon"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <button className="btn btn-primary" disabled={busy}>
              Tambah
            </button>
          </form>
        </Panel>

        <Panel
          title="Petakan ke resource"
          note="Satu resource boleh dipegang beberapa orang, dan satu orang boleh memegang beberapa resource. Yang utama hanya boleh satu."
        >
          <form onSubmit={addMapping}>
            <div className="filters">
              <select
                className="select"
                value={map.personId}
                onChange={(e) => setMap({ ...map, personId: e.target.value })}
                disabled={people.length === 0}
                required
              >
                <option value="">Pilih orang…</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.org ? ` — ${p.org}` : ''}
                  </option>
                ))}
              </select>
              <input
                className="input"
                list="team-known-resources"
                placeholder="ID resource programme"
                value={map.resourceId}
                onChange={(e) => setMap({ ...map, resourceId: e.target.value })}
                disabled={people.length === 0}
                required
              />
              <datalist id="team-known-resources">
                {[...known].map(([id, name]) => (
                  <option key={id} value={id} label={name} />
                ))}
              </datalist>
              <label className="hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={map.isPrimary}
                  onChange={(e) => setMap({ ...map, isPrimary: e.target.checked })}
                />
                Penanggung jawab utama
              </label>
            </div>
            <button className="btn btn-primary" disabled={busy || people.length === 0}>
              Petakan
            </button>
            {people.length === 0 && <p className="hint">Tambah orang lebih dulu.</p>}
          </form>

          <p className="panel-foot">
            Daftar resource programme belum tersedia lewat API, jadi kolom di atas menerima ID
            resource. Yang muncul sebagai saran hanyalah {fmtInt(known.size)} resource yang sudah
            pernah dipetakan seseorang — bukan seluruh resource proyek.
          </p>
        </Panel>
      </div>

      <Panel
        title="Terdaftar"
        note={
          people.length === 0
            ? 'Belum ada.'
            : `${fmtInt(people.length)} orang, memegang ${fmtInt(mapped.length)} resource di proyek ini.`
        }
        wide
      >
        {people.length === 0 ? (
          <p className="hint">Daftar akan terisi begitu orang pertama ditambahkan.</p>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Nama</th>
                  <th>Organisasi</th>
                  <th>Peran</th>
                  <th>Kontak</th>
                  <th>Resource yang dipegang</th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => {
                  const here = p.assignees.filter((a) => a.resource.projectId === projectId);
                  const elsewhere = p.assignees.length - here.length;
                  return (
                    <tr key={p.id} className="no-click">
                      <td className="num">{p.name}</td>
                      <td>{p.org ?? '—'}</td>
                      <td>{p.role ?? '—'}</td>
                      <td className="hint">{p.email ?? p.phone ?? '—'}</td>
                      <td>
                        {here.length === 0 ? (
                          <span className="hint">
                            belum dipetakan — namanya tidak akan muncul di papan mingguan
                          </span>
                        ) : (
                          here.map((a) => (
                            <div
                              key={a.id}
                              style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}
                            >
                              <span>{a.resource.name}</span>
                              {a.isPrimary && <span className="chip good">utama</span>}
                              {a.location && <span className="chip">{a.location.name}</span>}
                              {(a.fromDate || a.toDate) && (
                                <span className="hint">
                                  {fmtDate(a.fromDate)} – {fmtDate(a.toDate)}
                                </span>
                              )}
                              <button
                                className="btn btn-ghost btn-sm"
                                disabled={busy}
                                onClick={() => unmap(a.id)}
                              >
                                Lepas
                              </button>
                            </div>
                          ))
                        )}
                        {elsewhere > 0 && (
                          <span className="hint">{elsewhere} pemetaan lain di proyek lain</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
